import { validateUrl } from 'src/data/validation';

import { POPULAR_TLDS_STRING } from './popularTlds';
import { cleanUrl } from './utilities';

// Update: Added lookbehind to ensure the URL is not part of an email (not preceded by '@').
const HTTP_URL_REGEX =
  '(?<!@)https?:\\/\\/(?:www\\.)?\\w{1}[-a-zA-Z0-9@:%._\\+~#=]{0,256}\\.[a-zA-Z0-9()]{1,63}(?:[-a-zA-Z0-9@\\(\\):%_\\+.~#?&\\/=]*)?[.]?\\b';
const WWW_URL_REGEX =
  '(?<!@)www\\.[\\-a-zA-Z0-9]+(\\.{1}[\\-a-zA-Z0-9]+)*\\.([a-zA-Z]{2,5})+((\\/[\\S]+))?\\b';
const NON_HTTP_WWW_URL_REGEX =
  '(?<!@)\\b[\\-a-zA-Z0-9]+(\\.{1}[\\-a-zA-Z0-9]+)*\\.(' +
  POPULAR_TLDS_STRING +
  '){1}((\\/[\\S]+))?\\b';

const createRegex = (regexStr: string, content: string): string[] => {
  try {
    const regex = RegExp(regexStr, 'gu');
    const regexResults = content.match(regex);
    const result: string[] = [];

    if (regexResults && regexResults.length) {
      regexResults.forEach((match) => {
        if (match && validateUrl(match).valid === true) {
          result.push(match);
        }
      });
    }

    return result;
  } catch {
    return [];
  }
};

const removeDuplicateUrls = (urls: string[]): string[] => {
  const urlMap = new Map<string, string>();
  for (const url of urls) {
    const cleanedUrl = cleanUrl(url);
    if (!urlMap.has(cleanedUrl) || url.length > urlMap.get(cleanedUrl)!.length) {
      urlMap.set(cleanedUrl, url);
    }
  }
  return Array.from(urlMap.values());
};

const removeUnwantedParentheses = (urls: string[]): string[] => {
  for (let i = 0; i < urls.length; i++) {
    // if last characters is a closing parentheses and there is no opening parentheses remove the closing one
    if (urls[i].slice(-1) === ')' && !urls[i].includes('(')) {
      urls[i] = urls[i].slice(0, -1);
    }
  }
  return urls;
};

export const extractUrls = (
  content: string,
  limit: number
): { urls: string[]; overLimit: boolean } => {
  const httpRegexResult = createRegex(HTTP_URL_REGEX, content);
  const wwwUrlRegexResult = createRegex(WWW_URL_REGEX, content);
  const nonHttpRegexResult = createRegex(NON_HTTP_WWW_URL_REGEX, content);

  const urls = httpRegexResult.concat(wwwUrlRegexResult, nonHttpRegexResult);
  const urlsRemoveParentheses = removeUnwantedParentheses(urls);
  const urlsTrimmed = urlsRemoveParentheses.map((url) => url.trim());
  const urlsUnique = removeDuplicateUrls(urlsTrimmed);

  return { urls: urlsUnique.slice(0, limit), overLimit: urlsUnique.length > limit };
};

export const areAllUrlsExtracted = (
  urls: string[],
  urlsWithExtraction: Record<string, any>
): boolean => {
  return urls.every((url) => Object.prototype.hasOwnProperty.call(urlsWithExtraction, url));
};

export const getSuccessfulExtractions = (extractions: Record<string, any>): Record<string, any> => {
  return Object.fromEntries(Object.entries(extractions).filter(([_, value]) => value !== null));
};
